import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  MapIcon,
  ClockIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  UsersIcon,
  BuildingOfficeIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

interface DistrictComplianceData {
  district_id: number;
  district_name: string;
  total_centers: number;
  morning: {
    reported: number;
    percentage: number;
  };
  afternoon: {
    reported: number;
    percentage: number;
  };
  vote_count: {
    reported: number;
    percentage: number;
  };
  overall_compliance: number;
}

interface CenterReportingStatus {
  center_id: number;
  center_name: string;
  district_name: string;
  morning_reported: boolean;
  afternoon_reported: boolean;
  vote_count_reported: boolean;
  last_report_time?: string;
}

interface WardData {
  ward_id: number;
  ward_name: string;
  observer_name: string;
  centers: CenterData[];
}

interface CenterData {
  center_id: number;
  center_name: string;
  morning: {
    submitted: number;
    total: number;
  };
  afternoon: {
    submitted: number;
    total: number;
  };
  vote_count: {
    submitted: number;
    total: number;
  };
}

interface DistrictWardData {
  district_id: number;
  district_name: string;
  wards: WardData[];
}

// Transform API ward data to fix all timeline totals (keep original submitted values)
const transformWardData = (apiData: DistrictWardData): DistrictWardData => {
  return {
    ...apiData,
    wards: apiData.wards.map(ward => ({
      ...ward,
      centers: ward.centers.map(center => ({
        ...center,
        morning: {
          ...center.morning,
          total: 1, // Each center should submit 1 morning report
          // Keep original submitted value (0 or 1) from API
        },
        afternoon: {
          ...center.afternoon,
          total: 1, // Each center should submit 1 afternoon report
          // Keep original submitted value (0 or 1) from API
        },
        vote_count: {
          ...center.vote_count,
          total: 1, // Each center should submit 1 vote count report
          // Keep original submitted value (0 or 1) from API
        }
      }))
    }))
  };
};

// Dummy ward data generator
const getDummyWardData = (districtId: number): DistrictWardData => {
  const districtNames: { [key: number]: string } = {
    1: 'Lilongwe',
    2: 'Blantyre',
    3: 'Mzuzu',
    4: 'Zomba',
    5: 'Kasungu'
  };

  const wardNames = [
    'Central Ward', 'North Ward', 'South Ward', 'East Ward', 'West Ward',
    'Market Ward', 'Industrial Ward', 'Residential Ward', 'Commercial Ward', 'Rural Ward'
  ];

  const observerNames = [
    'John Banda', 'Mary Phiri', 'Peter Mwale', 'Grace Tembo', 'James Nyirenda',
    'Sarah Kachala', 'David Chirwa', 'Ruth Mvula', 'Michael Zulu', 'Agnes Mbewe'
  ];

  const centerNames = [
    'Primary School', 'Secondary School', 'Community Center', 'Church Hall', 'Market Hall',
    'Health Center', 'Youth Center', 'Sports Club', 'Town Hall', 'Village Hall'
  ];

  const wards: WardData[] = wardNames.slice(0, 5 + (districtId % 3)).map((wardName, wardIndex) => {
    const centers: CenterData[] = centerNames.slice(0, 3 + (wardIndex % 3)).map((centerName, centerIndex) => ({
      center_id: (districtId * 100) + (wardIndex * 10) + centerIndex,
      center_name: `${wardName} ${centerName}`,
      morning: {
        submitted: Math.floor(Math.random() * 2), // 0-1 submissions
        total: 1 // Always expect 1 morning report per center
      },
      afternoon: {
        submitted: Math.floor(Math.random() * 2), // 0-1 submissions
        total: 1 // Each center should submit 1 afternoon report
      },
      vote_count: {
        submitted: Math.floor(Math.random() * 2), // 0-1 submissions
        total: 1 // Each center should submit 1 vote count report
      }
    }));

    return {
      ward_id: (districtId * 10) + wardIndex,
      ward_name: wardName,
      observer_name: observerNames[wardIndex % observerNames.length],
      centers
    };
  });

  return {
    district_id: districtId,
    district_name: districtNames[districtId] || `District ${districtId}`,
    wards
  };
};

const ComplianceDashboard: React.FC = () => {
  const { user } = useAuth();
  const [selectedDistricts, setSelectedDistricts] = useState<number[]>(() => {
    // Load selected districts from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('complianceDashboard_selectedDistricts');
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });
  const [showDistrictFilter, setShowDistrictFilter] = useState(() => {
    // Load filter visibility state from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('complianceDashboard_showDistrictFilter');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null);
  const [selectedTimeline, setSelectedTimeline] = useState<'overview' | 'morning' | 'afternoon' | 'vote_count'>('overview');
  const [showWardDetails, setShowWardDetails] = useState(false);
  const [wardData, setWardData] = useState<DistrictWardData | null>(null);
  const [selectedDistrictName, setSelectedDistrictName] = useState<string | null>(null);

  // Save selectedDistricts to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('complianceDashboard_selectedDistricts', JSON.stringify(selectedDistricts));
    }
  }, [selectedDistricts]);

  // Save showDistrictFilter to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('complianceDashboard_showDistrictFilter', JSON.stringify(showDistrictFilter));
    }
  }, [showDistrictFilter]);

  // Dummy data for demonstration
  const dummyDistricts: DistrictComplianceData[] = [
    {
      district_id: 1,
      district_name: "Lilongwe",
      total_centers: 200,
      morning: { reported: 160, percentage: 80.0 },
      afternoon: { reported: 140, percentage: 70.0 },
      vote_count: { reported: 120, percentage: 60.0 },
      overall_compliance: 70.0
    },
    {
      district_id: 2,
      district_name: "Blantyre",
      total_centers: 180,
      morning: { reported: 162, percentage: 90.0 },
      afternoon: { reported: 153, percentage: 85.0 },
      vote_count: { reported: 144, percentage: 80.0 },
      overall_compliance: 85.0
    },
    {
      district_id: 3,
      district_name: "Mzuzu",
      total_centers: 120,
      morning: { reported: 96, percentage: 80.0 },
      afternoon: { reported: 84, percentage: 70.0 },
      vote_count: { reported: 72, percentage: 60.0 },
      overall_compliance: 70.0
    },
    {
      district_id: 4,
      district_name: "Zomba",
      total_centers: 150,
      morning: { reported: 135, percentage: 90.0 },
      afternoon: { reported: 120, percentage: 80.0 },
      vote_count: { reported: 105, percentage: 70.0 },
      overall_compliance: 80.0
    },
    {
      district_id: 5,
      district_name: "Kasungu",
      total_centers: 100,
      morning: { reported: 50, percentage: 50.0 },
      afternoon: { reported: 40, percentage: 40.0 },
      vote_count: { reported: 30, percentage: 30.0 },
      overall_compliance: 40.0
    },
    {
      district_id: 6,
      district_name: "Mangochi",
      total_centers: 140,
      morning: { reported: 98, percentage: 70.0 },
      afternoon: { reported: 84, percentage: 60.0 },
      vote_count: { reported: 70, percentage: 50.0 },
      overall_compliance: 60.0
    },
    {
      district_id: 7,
      district_name: "Dedza",
      total_centers: 110,
      morning: { reported: 99, percentage: 90.0 },
      afternoon: { reported: 88, percentage: 80.0 },
      vote_count: { reported: 77, percentage: 70.0 },
      overall_compliance: 80.0
    },
    {
      district_id: 8,
      district_name: "Ntcheu",
      total_centers: 95,
      morning: { reported: 76, percentage: 80.0 },
      afternoon: { reported: 67, percentage: 70.5 },
      vote_count: { reported: 57, percentage: 60.0 },
      overall_compliance: 70.2
    }
  ];

  // Dummy centers data based on selected district and timeline
  const getDummyCentersData = (districtId: number, timeline: string) => {
    const district = dummyDistricts.find(d => d.district_id === districtId);
    if (!district) return { reported: [], unreported: [] };

    // Handle timeline data safely
    let timelineData: { reported: number; percentage: number };
    if (timeline === 'overview' || timeline === 'morning') {
      timelineData = district.morning;
    } else if (timeline === 'afternoon') {
      timelineData = district.afternoon;
    } else if (timeline === 'vote_count') {
      timelineData = district.vote_count;
    } else {
      timelineData = district.morning; // fallback
    }

    const reportedCount = timelineData?.reported || 0;
    const unreportedCount = district.total_centers - reportedCount;

    const reported: CenterReportingStatus[] = Array.from({ length: Math.min(reportedCount, 10) }, (_, i) => ({
      center_id: i + 1,
      center_name: `${district.district_name} Center ${String.fromCharCode(65 + i)}`,
      district_name: district.district_name,
      morning_reported: timeline === 'morning' || Math.random() > 0.3,
      afternoon_reported: timeline === 'afternoon' || Math.random() > 0.4,
      vote_count_reported: timeline === 'vote_count' || Math.random() > 0.5,
      last_report_time: `${Math.floor(Math.random() * 2) + 1} hours ago`
    }));

    const unreported: CenterReportingStatus[] = Array.from({ length: Math.min(unreportedCount, 8) }, (_, i) => ({
      center_id: i + 100,
      center_name: `${district.district_name} Center ${String.fromCharCode(75 + i)}`,
      district_name: district.district_name,
      morning_reported: false,
      afternoon_reported: false,
      vote_count_reported: false,
    }));

    return { reported, unreported };
  };

  const { data: complianceData, isLoading: complianceLoading, error: complianceError } = useQuery(
    ['district-compliance'],
    () => api.dashboard.getDistrictCompliance(),
    { 
      refetchInterval: 30000,
      retry: 1,
      onSuccess: (data) => {
        console.log('✅ Live API data received:', data);
      },
      onError: (error) => {
        console.log('❌ API Error - using dummy data:', error);
      }
    }
  );

  const { data: centersData, isLoading: centersLoading } = useQuery(
    ['centers-reporting-status', selectedDistrict, selectedTimeline],
    () => api.dashboard.getCentersReportingStatus({
      district_id: selectedDistrict || undefined,
      timeline: selectedTimeline === 'overview' ? 'morning' : selectedTimeline
    }),
    {
      refetchInterval: 30000,
      enabled: !!selectedDistrict && selectedTimeline !== 'overview',
      retry: false,
      onError: () => {
        // Silently handle API error and use dummy data
      }
    }
  );

  // Fetch ward data when district is selected
  const { data: wardComplianceData, isLoading: wardDataLoading, error: wardDataError } = useQuery(
    ['district-ward-compliance', selectedDistrictName],
    () => {
      console.log('🚀 Fetching ward data for district:', selectedDistrictName);
      return api.dashboard.getDistrictWardCompliance(selectedDistrictName!);
    },
    {
      enabled: !!selectedDistrictName && showWardDetails,
      retry: 2,
      onSuccess: (response) => {
        console.log('✅ Ward data received:', response);
        if (response?.data?.data) {
          // Transform the API data to fix morning total count
          const transformedData = transformWardData(response.data.data);
          setWardData(transformedData);
        }
      },
      onError: (error) => {
        console.error('❌ Error fetching ward data:', error);
        // Fallback to dummy data on error
        if (selectedDistrict) {
          const dummyData = getDummyWardData(selectedDistrict);
          setWardData(dummyData);
        }
      }
    }
  );

  // Show loading only on first load
  if (complianceLoading && !complianceData) {
    return <LoadingSpinner fullScreen />;
  }

  // Debug the API response structure
  console.log('🔍 Full API Response:', complianceData);
  console.log('🔍 API Response Data:', complianceData?.data);
  console.log('🔍 Districts from API:', complianceData?.data?.data?.districts);
  
  // Use live API data - fix the data path based on your response structure
  const apiDistricts = complianceData?.data?.data?.districts;
  const allDistricts: DistrictComplianceData[] = apiDistricts && apiDistricts.length > 0 ? apiDistricts : dummyDistricts;
  
  // Filter districts based on selection
  const districts: DistrictComplianceData[] = selectedDistricts.length > 0 
    ? allDistricts.filter(district => selectedDistricts.includes(district.district_id))
    : allDistricts;
  
  console.log('🎯 Final districts being used:', {
    source: apiDistricts && apiDistricts.length > 0 ? 'Live API' : 'Dummy Data',
    count: districts.length,
    firstDistrict: districts[0]?.district_name
  });
  
  // Get centers data from API or dummy data
  const centersDataFromAPI = centersData?.data;
  const dummyCentersData = selectedDistrict ? getDummyCentersData(selectedDistrict, selectedTimeline) : { reported: [], unreported: [] };
  
  const reportedCenters: CenterReportingStatus[] = centersDataFromAPI?.reported || dummyCentersData.reported;
  const unreportedCenters: CenterReportingStatus[] = centersDataFromAPI?.unreported || dummyCentersData.unreported;

  const getComplianceColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getTimelineLabel = (timeline: string) => {
    switch (timeline) {
      case 'overview': return 'Overview';
      case 'morning': return 'Morning Reports';
      case 'afternoon': return 'Afternoon Reports';
      case 'vote_count': return 'Vote Count Reports';
      default: return timeline;
    }
  };

  // Use API totals if available and no districts are selected, otherwise calculate from filtered districts
  const getOrCalculateTotals = () => {
    // If districts are selected, always calculate from filtered districts
    if (selectedDistricts.length > 0) {
      const calculated = districts.reduce((acc, district) => {
        acc.totalCenters += district.total_centers;
        acc.morningReported += district.morning.reported;
        acc.afternoonReported += district.afternoon.reported;
        acc.voteCountReported += district.vote_count.reported;
        return acc;
      }, {
        totalCenters: 0,
        morningReported: 0,
        afternoonReported: 0,
        voteCountReported: 0
      });

      return {
        ...calculated,
        morningPercentage: calculated.totalCenters > 0 ? (calculated.morningReported / calculated.totalCenters) * 100 : 0,
        afternoonPercentage: calculated.totalCenters > 0 ? (calculated.afternoonReported / calculated.totalCenters) * 100 : 0,
        voteCountPercentage: calculated.totalCenters > 0 ? (calculated.voteCountReported / calculated.totalCenters) * 100 : 0
      };
    }

    // Check if API provides totals directly (only when no districts selected)
    const apiTotals = complianceData?.data?.data?.totals;
    if (apiTotals) {
      console.log('🎯 Using API totals:', apiTotals);
      return {
        totalCenters: apiTotals.total_centers,
        morningReported: apiTotals.morning.reported,
        afternoonReported: apiTotals.afternoon.reported,
        voteCountReported: apiTotals.vote_count.reported,
        morningPercentage: apiTotals.morning.percentage,
        afternoonPercentage: apiTotals.afternoon.percentage,
        voteCountPercentage: apiTotals.vote_count.percentage,
      };
    }

    // Fallback: calculate from all districts data
    const calculated = districts.reduce((acc, district) => {
      acc.totalCenters += district.total_centers;
      acc.morningReported += district.morning.reported;
      acc.afternoonReported += district.afternoon.reported;
      acc.voteCountReported += district.vote_count.reported;
      return acc;
    }, {
      totalCenters: 0,
      morningReported: 0,
      afternoonReported: 0,
      voteCountReported: 0
    });

    return {
      ...calculated,
      morningPercentage: calculated.totalCenters > 0 ? (calculated.morningReported / calculated.totalCenters) * 100 : 0,
      afternoonPercentage: calculated.totalCenters > 0 ? (calculated.afternoonReported / calculated.totalCenters) * 100 : 0,
      voteCountPercentage: calculated.totalCenters > 0 ? (calculated.voteCountReported / calculated.totalCenters) * 100 : 0
    };
  };

  const totals = getOrCalculateTotals();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="container px-4 py-8 mx-auto sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="relative overflow-hidden bg-white shadow-xl rounded-2xl">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 opacity-10"></div>
            <div className="relative px-8 py-12 text-center">
              <div className="flex items-center justify-center space-x-4 mb-4">
                <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-full">
                  <div className="w-2 h-2 mr-2 bg-indigo-600 rounded-full animate-pulse"></div>
                  Compliance Monitoring
                </div>
                <div className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${
                  apiDistricts && apiDistricts.length > 0 
                    ? 'text-green-700 bg-green-100' 
                    : 'text-orange-700 bg-orange-100'
                }`}>
                  <div className={`w-1.5 h-1.5 mr-1 rounded-full ${
                    apiDistricts && apiDistricts.length > 0 ? 'bg-green-500' : 'bg-orange-500'
                  }`}></div>
                  {apiDistricts && apiDistricts.length > 0 ? 'Live Data' : 'Demo Mode'}
                </div>
              </div>
              <h1 className="mb-4 text-4xl font-bold text-transparent xl:text-5xl bg-gradient-to-r from-gray-900 via-indigo-900 to-blue-900 bg-clip-text">
                District Compliance Dashboard
              </h1>
              <p className="max-w-2xl mx-auto text-lg text-gray-600 xl:text-xl">
                Track reporting compliance across all districts with real-time monitoring of morning, afternoon, and vote count submissions.
              </p>
            </div>
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* District Cards - Left Side (3 columns) */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {districts.map((district) => (
                <div
                  key={district.district_id}
                  className={`relative p-6 transition-all duration-300 transform bg-white border-2 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer ${
                    selectedDistrict === district.district_id 
                      ? 'border-indigo-300 ring-2 ring-indigo-200' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setSelectedDistrict(district.district_id);
                    setSelectedDistrictName(district.district_name);
                    setShowWardDetails(true);
                    // Clear previous ward data
                    setWardData(null);
                  }}
                >
                  {/* District Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="p-2 mr-3 bg-indigo-100 rounded-xl">
                        <MapIcon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{district.district_name}</h3>
                        <p className="text-sm text-gray-500">{district.total_centers} centers</p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getComplianceColor(district.overall_compliance)}`}>
                      {district.overall_compliance.toFixed(1)}%
                    </div>
                  </div>

                  {/* Timeline Progress Cards - Dynamic based on selected timeline */}
                  <div className="space-y-3">
                    {selectedTimeline === 'overview' ? (
                      // Show all three timelines
                      <>
                        {/* Morning */}
                        <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center">
                            <div className="w-3 h-3 mr-3 bg-yellow-500 rounded-full"></div>
                            <span className="text-sm font-medium text-gray-700">Morning</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-yellow-700">
                              {district.morning.percentage.toFixed(1)}%
                            </div>
                            <div className="text-xs text-yellow-600">
                              {district.morning.reported}/{district.total_centers}
                            </div>
                          </div>
                        </div>

                        {/* Afternoon */}
                        <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <div className="flex items-center">
                            <div className="w-3 h-3 mr-3 bg-orange-500 rounded-full"></div>
                            <span className="text-sm font-medium text-gray-700">Afternoon</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-orange-700">
                              {district.afternoon.percentage.toFixed(1)}%
                            </div>
                            <div className="text-xs text-orange-600">
                              {district.afternoon.reported}/{district.total_centers}
                            </div>
                          </div>
                        </div>

                        {/* Vote Count */}
                        <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-center">
                            <div className="w-3 h-3 mr-3 bg-purple-500 rounded-full"></div>
                            <span className="text-sm font-medium text-gray-700">Vote Count</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold text-purple-700">
                              {district.vote_count.percentage.toFixed(1)}%
                            </div>
                            <div className="text-xs text-purple-600">
                              {district.vote_count.reported}/{district.total_centers}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : selectedTimeline === 'morning' ? (
                      // Show only morning
                      <div className="flex items-center justify-between p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-4 h-4 mr-3 bg-yellow-500 rounded-full animate-pulse"></div>
                          <span className="text-base font-semibold text-gray-800">Morning Reports</span>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-yellow-700">
                            {district.morning.percentage.toFixed(1)}%
                          </div>
                          <div className="text-sm text-yellow-600">
                            {district.morning.reported} of {district.total_centers} centers
                          </div>
                        </div>
                      </div>
                    ) : selectedTimeline === 'afternoon' ? (
                      // Show only afternoon
                      <div className="flex items-center justify-between p-4 bg-orange-50 border-2 border-orange-300 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-4 h-4 mr-3 bg-orange-500 rounded-full animate-pulse"></div>
                          <span className="text-base font-semibold text-gray-800">Afternoon Reports</span>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-orange-700">
                            {district.afternoon.percentage.toFixed(1)}%
                          </div>
                          <div className="text-sm text-orange-600">
                            {district.afternoon.reported} of {district.total_centers} centers
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Show only vote count
                      <div className="flex items-center justify-between p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-4 h-4 mr-3 bg-purple-500 rounded-full animate-pulse"></div>
                          <span className="text-base font-semibold text-gray-800">Vote Count Reports</span>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-purple-700">
                            {district.vote_count.percentage.toFixed(1)}%
                          </div>
                          <div className="text-sm text-purple-600">
                            {district.vote_count.reported} of {district.total_centers} centers
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Selection Indicator */}
                  {selectedDistrict === district.district_id && (
                    <div className="absolute top-2 right-2">
                      <div className="w-3 h-3 bg-indigo-500 rounded-full animate-pulse"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right Sidebar - Center Lists */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Timeline Selector */}
              <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-lg">
                <h3 className="mb-3 text-lg font-semibold text-gray-900">Select Timeline</h3>
                <div className="space-y-2">
                  {(['overview', 'morning', 'afternoon', 'vote_count'] as const).map((timeline) => (
                    <button
                      key={timeline}
                      onClick={() => setSelectedTimeline(timeline)}
                      className={`w-full p-3 text-left rounded-lg transition-all duration-200 ${
                        selectedTimeline === timeline
                          ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                          : 'bg-gray-50 text-gray-700 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {timeline === 'overview' ? (
                            <ChartBarIcon className="w-4 h-4 mr-2" />
                          ) : (
                            <ClockIcon className="w-4 h-4 mr-2" />
                          )}
                          <span className="text-sm font-medium">{getTimelineLabel(timeline)}</span>
                        </div>
                        {timeline !== 'overview' && (
                          <div className="text-xs text-gray-500">
                            {timeline === 'morning' && `${totals.morningReported}/${totals.totalCenters}`}
                            {timeline === 'afternoon' && `${totals.afternoonReported}/${totals.totalCenters}`}
                            {timeline === 'vote_count' && `${totals.voteCountReported}/${totals.totalCenters}`}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* District Filter */}
              <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-lg">
                <h3 className="mb-3 text-lg font-semibold text-gray-900 flex items-center justify-between">
                  <div className="flex items-center">
                    <FunnelIcon className="w-5 h-5 mr-2" />
                    Filter Districts
                  </div>
                  <button
                    onClick={() => setShowDistrictFilter(!showDistrictFilter)}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    {showDistrictFilter ? 'Hide' : 'Show'}
                  </button>
                </h3>
                
                {/* Selected Districts Summary */}
                <div className="mb-3">
                  <div className="text-sm text-gray-600">
                    {selectedDistricts.length === 0 ? (
                      'All Districts Selected'
                    ) : (
                      `${selectedDistricts.length} District${selectedDistricts.length > 1 ? 's' : ''} Selected`
                    )}
                  </div>
                  {selectedDistricts.length > 0 && (
                    <button
                      onClick={() => {
                        setSelectedDistricts([]);
                        setShowDistrictFilter(false);
                        // Also clear from localStorage
                        if (typeof window !== 'undefined') {
                          localStorage.removeItem('complianceDashboard_selectedDistricts');
                          localStorage.removeItem('complianceDashboard_showDistrictFilter');
                        }
                      }}
                      className="text-xs text-red-600 hover:text-red-800 mt-1"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {(showDistrictFilter || selectedDistricts.length > 0) && (
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                    {allDistricts.map((district: DistrictComplianceData) => (
                      <label key={district.district_id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDistricts.includes(district.district_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDistricts([...selectedDistricts, district.district_id]);
                            } else {
                              setSelectedDistricts(selectedDistricts.filter(id => id !== district.district_id));
                            }
                          }}
                          className="text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">{district.district_name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Overall Statistics */}
              <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-lg">
                <h3 className="mb-3 text-lg font-semibold text-gray-900">
                  Overall Statistics
                  {selectedDistricts.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-indigo-600">
                      ({selectedDistricts.length === 1 
                        ? allDistricts.find(d => d.district_id === selectedDistricts[0])?.district_name 
                        : `${selectedDistricts.length} Districts`})
                    </span>
                  )}
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="w-4 h-4 mr-2 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Total Centers</span>
                    </div>
                    <span className="text-lg font-bold text-blue-700">{totals.totalCenters}</span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-yellow-50 border border-yellow-200 rounded">
                      <span className="text-xs text-gray-600">Morning Reported</span>
                      <div className="text-right">
                        <div className="text-sm font-bold text-yellow-700">{totals.morningPercentage.toFixed(1)}%</div>
                        <div className="text-xs text-yellow-600">{totals.morningReported}/{totals.totalCenters}</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-2 bg-orange-50 border border-orange-200 rounded">
                      <span className="text-xs text-gray-600">Afternoon Reported</span>
                      <div className="text-right">
                        <div className="text-sm font-bold text-orange-700">{totals.afternoonPercentage.toFixed(1)}%</div>
                        <div className="text-xs text-orange-600">{totals.afternoonReported}/{totals.totalCenters}</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-2 bg-purple-50 border border-purple-200 rounded">
                      <span className="text-xs text-gray-600">Vote Count Reported</span>
                      <div className="text-right">
                        <div className="text-sm font-bold text-purple-700">{totals.voteCountPercentage.toFixed(1)}%</div>
                        <div className="text-xs text-purple-600">{totals.voteCountReported}/{totals.totalCenters}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selected District Info */}
              {selectedDistrict && (
                <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-lg">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Selected District</h3>
                    <p className="text-sm text-gray-600">
                      {districts.find(d => d.district_id === selectedDistrict)?.district_name}
                    </p>
                  </div>

                  {centersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Reported Centers */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-green-700">Reported Centers</h4>
                          <span className="px-2 py-1 text-xs font-bold text-green-700 bg-green-100 rounded-full">
                            {reportedCenters.length}
                          </span>
                        </div>
                        <div className="max-h-32 overflow-y-auto">
                          {reportedCenters.length > 0 ? (
                            <div className="space-y-1">
                              {reportedCenters.map((center) => (
                                <div key={center.center_id} className="flex items-center p-2 text-xs bg-green-50 border border-green-200 rounded">
                                  <CheckCircleIcon className="w-3 h-3 mr-2 text-green-500" />
                                  <span className="text-green-700">{center.center_name}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">No centers reported yet</p>
                          )}
                        </div>
                      </div>

                      {/* Unreported Centers */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-red-700">Not Reported</h4>
                          <span className="px-2 py-1 text-xs font-bold text-red-700 bg-red-100 rounded-full">
                            {unreportedCenters.length}
                          </span>
                        </div>
                        <div className="max-h-32 overflow-y-auto">
                          {unreportedCenters.length > 0 ? (
                            <div className="space-y-1">
                              {unreportedCenters.map((center) => (
                                <div key={center.center_id} className="flex items-center p-2 text-xs bg-red-50 border border-red-200 rounded">
                                  <ExclamationCircleIcon className="w-3 h-3 mr-2 text-red-500" />
                                  <span className="text-red-700">{center.center_name}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-green-600">All centers reported!</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Instructions */}
              {!selectedDistrict && (
                <div className="p-4 bg-white border border-gray-200 rounded-2xl shadow-lg">
                  <div className="text-center">
                    <BuildingOfficeIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a District</h3>
                    <p className="text-sm text-gray-600">
                      Click on any district card to view detailed reporting status for individual centers.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ward Details Modal */}
      {showWardDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {selectedDistrictName || 'District'} - Ward Details
                </h2>
                <p className="text-indigo-100 mt-1">
                  {wardDataLoading
                    ? 'Loading ward information...'
                    : wardData
                      ? `${wardData.wards?.length || 0} wards with observers and centers`
                      : 'Ward information'
                  }
                </p>
              </div>
              <button
                onClick={() => {
                  setShowWardDetails(false);
                  setWardData(null);
                  setSelectedDistrictName(null);
                }}
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {wardDataLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading ward details...</p>
                  </div>
                </div>
              ) : wardDataError ? (
                <div className="text-center py-12">
                  <div className="text-red-500 mb-4">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Ward Data</h3>
                  <p className="text-gray-600 mb-4">There was an error loading the ward information.</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Retry
                  </button>
                </div>
              ) : wardData && wardData.wards ? (
                <div className="space-y-6">
                  {wardData.wards.map((ward) => (
                  <div key={ward.ward_id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    {/* Ward Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          {ward.ward_name}
                        </h3>
                        <p className="text-sm text-indigo-600 font-medium">
                          Observer: {ward.observer_name}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Centers</p>
                        <p className="text-2xl font-bold text-gray-900">{ward.centers.length}</p>
                      </div>
                    </div>

                    {/* Centers Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {ward.centers.map((center) => (
                        <div key={center.center_id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                          {/* Center Header */}
                          <div className="mb-3">
                            <h4 className="font-medium text-gray-900 text-sm">
                              {center.center_name}
                            </h4>
                          </div>

                          {/* Report Stats */}
                          <div className="space-y-2">
                            {/* Morning */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Morning</span>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-gray-900">
                                  {center.morning.submitted}/{center.morning.total}
                                </span>
                                <div className={`w-2 h-2 rounded-full ${
                                  center.morning.submitted === center.morning.total
                                    ? 'bg-green-500'
                                    : center.morning.submitted > 0
                                      ? 'bg-yellow-500'
                                      : 'bg-red-500'
                                }`}></div>
                              </div>
                            </div>

                            {/* Afternoon */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Afternoon</span>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-gray-900">
                                  {center.afternoon.submitted}/{center.afternoon.total}
                                </span>
                                <div className={`w-2 h-2 rounded-full ${
                                  center.afternoon.submitted === center.afternoon.total
                                    ? 'bg-green-500'
                                    : center.afternoon.submitted > 0
                                      ? 'bg-yellow-500'
                                      : 'bg-red-500'
                                }`}></div>
                              </div>
                            </div>

                            {/* Vote Count */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Vote Count</span>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-gray-900">
                                  {center.vote_count.submitted}/{center.vote_count.total}
                                </span>
                                <div className={`w-2 h-2 rounded-full ${
                                  center.vote_count.submitted === center.vote_count.total
                                    ? 'bg-green-500'
                                    : center.vote_count.submitted > 0
                                      ? 'bg-yellow-500'
                                      : 'bg-red-500'
                                }`}></div>
                              </div>
                            </div>
                          </div>

                          {/* Overall Status */}
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Overall</span>
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                (center.morning.submitted + center.afternoon.submitted + center.vote_count.submitted) ===
                                (center.morning.total + center.afternoon.total + center.vote_count.total)
                                  ? 'bg-green-100 text-green-800'
                                  : (center.morning.submitted + center.afternoon.submitted + center.vote_count.submitted) > 0
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                              }`}>
                                {Math.round(((center.morning.submitted + center.afternoon.submitted + center.vote_count.submitted) /
                                (center.morning.total + center.afternoon.total + center.vote_count.total)) * 100)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Ward Data Available</h3>
                  <p className="text-gray-600">No ward information found for this district.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceDashboard;